"use client";

import { useMemo } from "react";
import {
  assertLaunchPayloadSize,
  stripThumbnailsFromLaunchPayload,
} from "./launchPayloadFilter";
import { useMetaPlatform } from "./provider";

// --- Asset Operations ---

export function usePreviewAssets() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.previewAssets.useMutation();
}

export function useValidateAssets() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.validateAssets.useMutation();
}

// --- Batch Creation & Launch ---

export function useCreateBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.createBatch.useMutation();
}

export function usePreflightMeta() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.preflightMeta.useMutation();
}

export function useValidateMetaConfig() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.validateMetaConfig.useMutation();
}

export function useLogUnmappedError() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.logUnmappedValidationError.useMutation();
}

/**
 * v2 launch — hits `creativeTestingRouter.v2.launchWithAssets` instead of the
 * retired v1 path. Carries the TikTok schedule injection
 * (scheduleType=SCHEDULE_START_END + formatted start/end times).
 */
export function useLaunchBatchV2() {
  const { trpc } = useMetaPlatform();
  const base = trpc.creativeTestingRouter.v2.launchWithAssets.useMutation();

  return useMemo(() => {
    type MutatePayload = Parameters<typeof base.mutate>[0];

    const prepare = (payload: MutatePayload): MutatePayload => {
      const stripped = stripThumbnailsFromLaunchPayload(payload);
      assertLaunchPayloadSize(stripped);
      return stripped;
    };

    return {
      ...base,
      mutate: (
        payload: MutatePayload,
        options?: Parameters<typeof base.mutate>[1],
      ) => base.mutate(prepare(payload), options),
      mutateAsync: (
        payload: MutatePayload,
        options?: Parameters<typeof base.mutateAsync>[1],
      ) => base.mutateAsync(prepare(payload), options),
    };
  }, [base]);
}

/**
 * v2 batch creation — hits `creativeTestingRouter.v2.createBatch` instead of
 * the flat v1 `createBatch`. Mirror of `useLaunchBatchV2` for the create step:
 * the v2 procedure understands every platform (including Snapchat), whereas the
 * v1 one only handles Meta/TikTok/Google. See `V2_CREATE_BATCH_PLATFORMS`.
 */
export function useCreateBatchV2() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.v2.createBatch.useMutation();
}

/**
 * Platforms whose batch CREATION goes through the v2 procedure
 * (`useCreateBatchV2`) rather than the flat v1 `createBatch`.
 *
 * Launch is already fully on v2 for every platform (`useLaunchBatchV2`); batch
 * creation is the last step still defaulting to v1, so it's migrated
 * platform-by-platform through this single switch. To move a platform onto v2,
 * add it here — but first confirm `v2.createBatch` accepts that platform's
 * create payload. (Meta, for example, still sends
 * `launchSettingsOverrides.descriptions`, which the v2 schema doesn't model
 * yet — that gap must close before Meta can be added.)
 */
export const V2_CREATE_BATCH_PLATFORMS: ReadonlySet<string> = new Set([
  "SNAPCHAT",
]);

export type V2CreateBatchPlatform = "SNAPCHAT";

export function isV2CreateBatchPlatform(
  platform: string,
): platform is V2CreateBatchPlatform {
  return V2_CREATE_BATCH_PLATFORMS.has(platform);
}

// --- Batch Management ---

export function useDeleteBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.deleteBatch.useMutation();
}

// --- Planned Batches ---

export function useCreatePlannedBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.createPlannedBatch.useMutation();
}

export function useUpdatePlannedBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.updatePlannedBatch.useMutation();
}

export function useSaveDraftAssets() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.saveDraftAssets.useMutation();
}

// --- AI ---

export function useGenerateBatchObjective() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.generateBatchObjective.useMutation();
}

// --- Copy Presets ---

export function useCreateCopyPreset() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.createCopyPreset.useMutation();
}

export function useUpdateCopyPreset() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.updateCopyPreset.useMutation();
}

export function useDeleteCopyPreset() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.deleteCopyPreset.useMutation();
}
